<!-- Базовая карточка ЭДО-документа по паттерну информационных карточек рейса. -->
<template>
  <div class="edo-document-summary">
    <BaseInfoCard title="Внутренняя информация" class="edo-document-summary__card">
      <template #icon>
        <AppIcon name="clipboardCheck" :size="20" />
      </template>

      <BaseFormSection :columns="1" hide-title>
        <BaseFormField label="Интеграция ЭДО">
          <EdoDocumentIntegrationCard
            :integration-card="document.integration_card"
            :integration-label="document.integration_label"
          />
        </BaseFormField>

        <BaseFormField
          label="Рабочий статус"
          for-id="edo-internal-status"
          info="Помогает команде отметить результат работы с документом. Не меняет статус документа в ЭДО."
        >
          <template #action>
            <BaseActionLink
              :to="referenceItemsRoute"
              target="_self"
              title="Настроить рабочие статусы"
              aria-label="Настроить рабочие статусы"
            >
              <AppIcon name="settings" :size="16" />
            </BaseActionLink>
          </template>
          <BaseStatusSelect
            id="edo-internal-status"
            :value="statusId"
            :label="currentStatus.name"
            :color="currentStatus.color"
            :options="statuses"
            @change="changeStatus"
          />
        </BaseFormField>
      </BaseFormSection>

      <BaseFormSection :columns="1" hide-title>
        <BaseFormField
          label="Что решили по документу"
          for-id="edo-internal-resolution"
          info="Внутренняя заметка для команды. Не передаётся контрагентам и в ЭДО."
        >
          <base-textarea
            id="edo-internal-resolution"
            v-model="resolution"
            :disabled="isSubmitting"
            placeholder="Внутренняя заметка — не передаётся в ЭДО"
          />
        </BaseFormField>
      </BaseFormSection>

      <template #footer>
        <base-primary-button
          :disabled="isSubmitting || !resolutionChanged"
          @click="saveResolution"
        >
          {{ isSubmitting ? "Сохраняем..." : "Сохранить" }}
        </base-primary-button>
      </template>
    </BaseInfoCard>

    <BaseInfoCard title="Связи" class="edo-document-summary__card">
      <template #icon>
        <AppIcon name="layers" :size="20" />
      </template>

      <BaseFormSection :columns="1" hide-title>
        <BaseFormField label="Заказ" for-id="edo-source-order" wide>
          <OrderSelect
            id="edo-source-order"
            :model-value="sourceOrderId"
            :selected-entity="sourceOrder"
            selected-as-card
            :disabled="isSubmitting"
            placeholder="Выберите связанный заказ"
            @change="handleOrderChange"
            @select="handleOrderSelect"
          />
        </BaseFormField>

        <BaseFormField label="Рейс" for-id="edo-source-trip" wide>
          <TripSelect
            id="edo-source-trip"
            :model-value="sourceTripId"
            :selected-entity="sourceTrip"
            selected-as-card
            :order-id="sourceOrderId"
            :disabled="isSubmitting"
            placeholder="Выберите связанный рейс"
            @change="handleTripChange"
            @select="handleTripSelect"
          />
        </BaseFormField>
      </BaseFormSection>

      <template #footer>
        <base-primary-button
          :disabled="isSubmitting || !sourceLinksChanged"
          @click="$emit('update-source-links', { orderId: sourceOrderId, tripId: sourceTripId })"
        >
          Сохранить связи
        </base-primary-button>
      </template>
    </BaseInfoCard>
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import EdoDocumentIntegrationCard from "@/components/Edo/EdoDocumentIntegrationCard.vue";
import OrderSelect from "@/components/Order/OrderSelect.vue";
import TripSelect from "@/components/Trip/TripSelect.vue";
import referenceItemsApi from "@/services/api/referenceItemsApi";
import { getCompanyId } from "@/utils/userUtils";

export default {
  name: "EdoDocumentInternalStatus",

  components: {
    AppIcon,
    EdoDocumentIntegrationCard,
    OrderSelect,
    TripSelect,
  },

  props: {
    document: { type: Object, required: true },
    isSubmitting: { type: Boolean, default: false },
  },

  emits: ["save", "update-source-links"],

  data() {
    return {
      companyId: getCompanyId(),
      sourceOrderId: this.document.order_id || null,
      sourceTripId: this.document.trip_id || null,
      sourceOrder: this.document.order || null,
      sourceTrip: this.document.trip || null,
      resolution: this.document.internal_resolution || "",
      statusId: this.document.internal_status_id
        ? String(this.document.internal_status_id)
        : null,
      statuses: [],
    };
  },

  computed: {
    currentStatus() {
      return this.statuses.find((status) => status.value === this.statusId) || {
        name: this.document.internal_status?.name || "Не указан",
        color: this.document.internal_status?.color || "#64748b",
      };
    },

    resolutionChanged() {
      return this.resolution !== (this.document.internal_resolution || "");
    },

    sourceLinksChanged() {
      return String(this.sourceOrderId || "") !== String(this.document.order_id || "") ||
        String(this.sourceTripId || "") !== String(this.document.trip_id || "");
    },

    referenceItemsRoute() {
      return {
        name: "ReferenceItems",
        query: {
          target_model: "Edo::Document",
          target_field: "internal_status_id",
          title: "Статусы ЭДО",
        },
      };
    },

  },

  watch: {
    "document.internal_status_id"(value) {
      this.statusId = value ? String(value) : null;
    },
    "document.internal_resolution"(value) {
      this.resolution = value || "";
    },
    "document.order_id"(value) {
      this.sourceOrderId = value || null;
    },
    "document.trip_id"(value) {
      this.sourceTripId = value || null;
    },
    "document.order"(value) {
      this.sourceOrder = value || null;
    },
    "document.trip"(value) {
      this.sourceTrip = value || null;
    },
  },

  async mounted() {
    const response = await referenceItemsApi.index(this.companyId, {
      "q[target_model]": "Edo::Document",
      "q[target_field]": "internal_status_id",
      "q[enabled]": "true",
    });
    this.statuses = (response.data.result || response.data || []).map((item) => ({
      value: String(item.id),
      label: item.name,
      name: item.name,
      color: item.color || "#64748b",
    }));
  },

  methods: {
    changeStatus(statusId) {
      if (this.isSubmitting || statusId === this.statusId) return;
      this.statusId = statusId;
      this.$emit("save", { internal_status_id: statusId });
    },
    saveResolution() {
      if (this.isSubmitting || !this.resolutionChanged) return;
      this.$emit("save", { internal_resolution: this.resolution });
    },
    handleOrderChange(orderId) {
      if (String(orderId || "") === String(this.sourceOrderId || "")) return;
      this.sourceOrderId = orderId || null;
      this.sourceTripId = null;
      this.sourceTrip = null;
    },
    handleOrderSelect(order) {
      this.sourceOrder = order;
    },
    handleTripChange(tripId) {
      this.sourceTripId = tripId || null;
    },
    handleTripSelect(trip) {
      this.sourceTrip = trip;
    },
  },
};
</script>

<style scoped>
.edo-document-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  min-width: 0;
}

.edo-document-summary__card {
  min-width: 0;
  margin: 0;
}

.edo-document-summary :deep(.info-card),
.edo-document-summary :deep(.card-content),
.edo-document-summary :deep(.card-footer),
.edo-document-summary :deep(.base-form-section),
.edo-document-summary :deep(.form-layout__row),
.edo-document-summary :deep(.form-group) {
  min-width: 0;
}

.edo-document-summary :deep(.form-group) {
  overflow-wrap: anywhere;
}
</style>
